import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useEntityRow, useEntitySchema } from "@/features/backoffice";
import { ArrowSquareOutIcon } from "@phosphor-icons/react";
import { Link, useParams } from "react-router-dom";
import { EntityDetails } from "../components/entity-details";
import { EntityHistory } from "../components/entity-history";
import { EntityTable } from "../components/entity-table";
import { AdminPageLayout } from "../layout";

/**
 * One row of any entity: its fields, its audit trail, and what points at it.
 *
 * The "related" section is the reverse of the grid's foreign-key links —
 * {@link FOREIGN_KEYS} says `sessions.customer_id → customers`, so from a
 * customer we can offer the sessions that name it. It is the difference between
 * a row viewer and something you can actually investigate with.
 */

/**
 * Reverse relations: what points *at* this row, and through which column.
 *
 * The grid's {@link FOREIGN_KEYS} goes one way (`sessions.customer_id →
 * customers`); this is the other, and it is curated rather than derived —
 * `created_by` points at `users` from almost every table, and listing all of
 * those on a user's page would bury the two relations anyone came for.
 */
const RELATED_BY_TABLE: Record<string, { table: string; column: string }[]> = {
  clients: [
    { table: "client_users", column: "client_id" },
    { table: "client_agents", column: "client_id" },
    { table: "sessions", column: "client_id" },
  ],
  users: [{ table: "client_users", column: "user_id" }],
  customers: [
    { table: "sessions", column: "customer_id" },
    { table: "calendar_events", column: "customer_id" },
    { table: "qualifications", column: "customer_id" },
  ],
  sessions: [
    { table: "session_messages", column: "session_id" },
    { table: "session_reminders", column: "session_id" },
  ],
  client_agents: [
    { table: "sessions", column: "agent_id" },
    { table: "sessions_connectors", column: "agent_id" },
  ],
  ragdocuments: [{ table: "ragchunks", column: "document_id" }],
  qualification_forms: [{ table: "qualifications", column: "form_id" }],
  sessions_connectors: [{ table: "sessions", column: "connector_id" }],
};

export function AdminEntityDetailPage() {
  const { table = "", id = "" } = useParams<{ table: string; id: string }>();
  const { data: schema } = useEntitySchema(table);
  const { data: row, isLoading, error } = useEntityRow(table, id);

  const title =
    (schema?.title_column && (row?.[schema.title_column] as string)) || id;
  const related = RELATED_BY_TABLE[table] ?? [];
  const clientId = row?.client_id as string | undefined;

  return (
    <AdminPageLayout
      title={title}
      backTo={{
        label: schema?.label || table,
        path: `/admin/explorer/${table}`,
      }}
      description={
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs">
            {table} · {id}
          </span>
          {row?.is_deleted ? (
            <Badge variant="outline" color="red">
              supprimée
            </Badge>
          ) : null}
        </span>
      }
      actions={
        clientId ? (
          <Link to={`/admin/tenants/${clientId}`}>
            <Button variant="outline" size="sm">
              <ArrowSquareOutIcon className="h-4 w-4 mr-2" />
              Voir le tenant
            </Button>
          </Link>
        ) : null
      }
    >
      {error ? (
        <div className="rounded-sm border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {(error as Error).message}
        </div>
      ) : (
        <div className="space-y-6">
          <EntityDetails schema={schema} row={row} isLoading={isLoading} />

          <EntityHistory table={table} id={id} schema={schema} />

          {related.map((relation) => (
            <Card key={`${relation.table}:${relation.column}`}>
              <CardHeader>
                <CardTitle className="text-base">
                  {relation.table}
                  <span className="ml-2 font-mono text-xs font-normal text-muted-foreground">
                    {relation.column} = {id}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <EntityTable
                  table={relation.table}
                  implicitFilters={{
                    [relation.column]: { operator: "eq", value: id },
                  }}
                  defaultPageSize={10}
                  enableDeletedToggle={false}
                  hideSearch
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AdminPageLayout>
  );
}
